import { alpha } from "@mui/material";

/**
 * The track + pill fills for a segmented toggle (`@repo/ui`'s `ToggleButton`, and
 * the platform's `SegmentedTabs` — see the note at the bottom).
 *
 * ## Why these are functions of the theme rather than two constants
 *
 * A segmented control is read as THREE stacked surfaces: the surface it is
 * mounted on, a recessed track, and a raised pill. What makes it legible is the
 * ORDER of those three, not any one of their values — so every one of them has
 * to be expressed relative to the surface underneath, and that surface is not
 * knowable at authoring time.
 *
 * Writing them as absolute tokens is what broke the control. `background.paper`
 * is the correct pill fill only while the control sits on `background.default`;
 * mounted inside a `Card` — which IS `background.paper` — the pill and its own
 * container resolve to the same colour and the pill disappears. Measured on
 * Studio's "Logo alignment", dark mode: card `rgb(15,15,18)`, pill
 * `rgb(15,15,18)`. The pill was rendering, correctly positioned and fully
 * opaque, and was invisible.
 *
 * ## Why the dark branch is not a mirror of the light one
 *
 * The light track is a BLACK wash, which is the right direction there: it
 * recesses the groove below a white surface. Applied to a near-black surface the
 * same wash is a no-op — `alpha(black, 0.03)` over `#0f0f12` is a step of under
 * half a channel value — so in dark mode the track has to wash the other way, to
 * WHITE, and the pill has to be lighter still to stay above it.
 *
 * Composited over the platform's dark card (`#0f0f12`) this gives an ascending
 * ramp: card `#0f0f12` -> track `#1b1b1e` -> pill `#323234`. Over the bare page
 * (`#09090b`, for a control not in a card) it gives `#09090b` -> `#151517` ->
 * `#2c2c2e`. Both orderings hold because the washes composite over whatever is
 * beneath them, which is the property an absolute token does not have.
 *
 * ## Why ALPHA rather than a palette ramp
 *
 * `theme.palette.grey[100]` would also read in dark mode (the platform remaps
 * its neutral ramp onto zinc, so it is `#27272a` there). It was not used because
 * this is a layer-1 module rendered under either app's theme
 * (working-conventions §47) and a ramp index means different things under the
 * two: the platform reverses its neutrals in dark mode, `apps/app` never
 * overrides MUI's default grey at all. `common.white`/`common.black` are
 * guaranteed by MUI on every theme and mean the same thing everywhere — the
 * same reasoning that moved the pill off the custom `white[100]` key, which a
 * consumer theme that did not declare it resolved to no fill at all.
 *
 * ⚠️ The platform's `SegmentedTabs` is a SECOND implementation of this same
 * design and does NOT read from here yet. It hardcodes `grey[100]` for its track
 * and `background.paper` for its pill, so in dark mode its pill renders DARKER
 * than its track — visible, but inverted. Converging it is a real fix but it
 * moves 18 consumer surfaces, so it is deliberately not bundled into a bug fix
 * for a different control (working-conventions §69 makes exactly this argument
 * about `MuiAutocomplete.paper`). This module is where it should land when it
 * is done, so that the "these two are one design" claim becomes true rather
 * than aspirational — it was that claim, asserted while the two were in fact
 * separate, that let this regression through.
 */

/** Minimal structural theme shape — see `fieldChrome`/`navChrome` for why not `Theme`. */
type SegmentedTheme = {
  palette: {
    mode: string;
    common: { white: string; black: string };
    background: { paper: string };
  };
};

/**
 * The recessed groove the pill travels in.
 *
 * The light value is verbatim what it has been since it was calibrated: lighter
 * than `grey[100]`'s `#F5F5F5` without falling to `grey[50]`'s `#FAFAFA`, which
 * was already tried and left a `background.paper` pill at a ~1% step in
 * `apps/app`. Light rendering is unchanged by this module.
 */
export const segmentedTrackBg = (theme: SegmentedTheme): string =>
  theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.05)
    : alpha(theme.palette.common.black, 0.03);

/**
 * The raised pill that carries the selection.
 *
 * Light stays on `background.paper` — white is the ceiling in a light theme, so
 * there is nothing above it to reach for and the token is exactly right. Dark
 * has no such ceiling, so it takes a wash that composites above the track.
 */
export const segmentedPillBg = (theme: SegmentedTheme): string =>
  theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.1)
    : theme.palette.background.paper;

/**
 * The pill's drop shadow.
 *
 * `none` in dark mode, and that is not a shortcut. The shadow is a near-black
 * (`rgba(16,24,40,…)`), which is how a shadow makes a white chip look lifted off
 * a light track — but on a dark track it is painting dark on dark and
 * contributes nothing measurable. In dark mode the fill step alone carries the
 * elevation, which is what the ramp above is sized to do.
 */
export const segmentedPillShadow = (theme: SegmentedTheme): string =>
  theme.palette.mode === "dark"
    ? "none"
    : "0px 1px 2px rgba(16, 24, 40, 0.10), 0px 1px 3px rgba(16, 24, 40, 0.06)";
